import { exactInteger } from '@/lib/exact'
import type {
  Block, Cell, ChipCell, Column, DecideReason, FormBlock, FormLine, TableBlock,
} from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { Caller } from './handles'
import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, submitWrite, writeRefusal } from './writes'

/**
 * Pricing › Assignments (`pr-assign`), from `GET /v1/admin/price-list-assignments`.
 *
 * # This screen was refused until the read existed, and that was the right call
 *
 * Both write routes have existed for a long time and **nothing listed what was
 * assigned**. An operator could assign a price list and never see the result —
 * which is go-wallet#862's rule from the other end: *a control is fabricated
 * capability unless the operator can observe its effect.* The screen was left
 * unbuilt rather than shipped blind.
 *
 * go-wallet#891 added the collection read, so both halves exist and the screen
 * carries the controls.
 *
 * # `governs` is a claim this response makes carefully, and the screen must not widen it
 *
 * True **only** on an open CUSTOMER assignment: nothing outranks the customer
 * rung, so that claim needs no other evidence.
 *
 * A SEGMENT row never says it governs, however strong its precedence looks.
 * This query reads no segment membership, so *"the lowest-precedence segment
 * assignment is the one that wins BETWEEN a customer's own segments — and
 * calling it governing would assert it prices customers it does not price."*
 *
 * So a segment row renders what it is — a rung with an order — and never a
 * verdict. What one customer actually pays is answered by the per-customer
 * read, from the same function the fee path calls, and the screen says where.
 *
 * # Precedence is lower-is-stronger, and only on the segment rung
 *
 * There is nothing to order against on the customer rung, so the field is
 * absent there. Rendering `0` for it would invent the strongest possible
 * precedence on exactly the rows that do not have one.
 *
 * # Closed assignments are returned, and that is invariant 9
 *
 * A closed row is what explains a past fee event. A listing of only what is
 * open today hides every row a past charge resolved against, which is the
 * question this table gets asked six months later.
 */

export const PRICE_ASSIGNMENTS_PATH = '/price-list-assignments'

const SOURCE = 'postgres · price_list_assignments (GET /v1/admin/price-list-assignments)'
const AUTHORITY = 'operational'

/** `price_list.assign` — both verbs. Growth and Ops. */
const PERM = 'price_list.assign'
const ROLE = 'Growth or Ops'

interface Assignment {
  readonly id: number | null
  readonly scope: string
  readonly customerId: number | null
  readonly segmentKey: string
  /** Segment rung only. Lower is stronger. Absent on the customer rung. */
  readonly precedence: number | null
  readonly listCode: string
  readonly effectiveFrom: string
  readonly effectiveTo: string
  readonly open: boolean | null
  /** SERVED, and true only on an open customer assignment. Never derived. */
  readonly governs: boolean | null
  readonly createdBy: string
  readonly reasonCode: string
  readonly closedBy: string
  readonly closeReasonCode: string
}

function readAssignment(raw: unknown): Assignment | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const id = int(o['id'])
  if (id === null) return null
  return {
    id,
    scope: str(o['scope']),
    customerId: int(o['customer_id']),
    segmentKey: str(o['segment_key']),
    precedence: int(o['precedence']),
    listCode: str(o['list_code']),
    effectiveFrom: str(o['effective_from']),
    effectiveTo: str(o['effective_to']),
    open: typeof o['open'] === 'boolean' ? o['open'] : null,
    governs: typeof o['governs'] === 'boolean' ? o['governs'] : null,
    createdBy: str(o['created_by']),
    reasonCode: str(o['reason_code']),
    closedBy: str(o['closed_by']),
    closeReasonCode: str(o['close_reason_code']),
  }
}

const COLS: readonly Column[] = [
  ['#', 'm', '80px'],
  ['Who', 'x', 'minmax(170px,1.2fr)'],
  ['Rung', 'c', '150px'],
  ['Price list', 'm', '140px'],
  ['Does it price them', 'c', 'minmax(210px,1.4fr)'],
  ['From', 'm', '116px'],
  ['Until', 'm', '130px'],
  ['Set by', 'x', 'minmax(150px,1.1fr)'],
  ['Closed by', 'x', 'minmax(150px,1.1fr)'],
]

function whoCell(a: Assignment): Cell {
  if (a.scope === 'customer') {
    return a.customerId === null ? 'customer — NOT NAMED' : `customer ${String(a.customerId)}`
  }
  if (a.scope === 'segment') {
    return a.segmentKey === '' ? 'segment — NOT NAMED' : `segment ${a.segmentKey}`
  }
  return a.scope === '' ? '— not stated' : a.scope
}

/**
 * The rung, with its precedence where a precedence exists.
 *
 * Absent on the customer rung because there is nothing to order against, and
 * rendering `0` there would invent the strongest possible precedence on exactly
 * the rows that have none.
 */
function rungCell(a: Assignment): ChipCell {
  if (a.scope === 'customer') return ['customer — outranks all', 'g']
  if (a.scope === 'segment') {
    return a.precedence === null
      ? ['segment — no order stated', 'r']
      : [`segment · precedence ${String(a.precedence)}`, 'n']
  }
  return ['— not stated', 'r']
}

/**
 * Whether this row prices anybody, and the one thing this screen may not say.
 *
 * `governs` is served and is true only on an open customer assignment. A
 * segment row is rendered as what it is — a rung that competes with the
 * customer's other segments — and never as a verdict, because this read knows
 * no segment membership and cannot tell whether this customer is in it.
 */
function governsCell(a: Assignment): ChipCell {
  if (a.open === false) return ['no — closed', 'n']
  // SCOPE IS CHECKED BEFORE THE FLAG, and the order is the guarantee. Written
  // the other way round — `governs === true` first — a segment row arriving
  // with the flag set renders "this prices them", which is precisely the claim
  // the backend refuses to make and cannot support: this read carries no
  // segment membership. A test asserts it, because the first version of this
  // function had the branches in the convenient order and failed open.
  if (a.scope === 'segment') return ['only if it is their strongest segment', 'a']
  if (a.governs === true) return ['YES — this prices them', 'g']
  if (a.governs === null) return ['— not stated', 'r']
  return ['no', 'n']
}

function row(a: Assignment): readonly Cell[] {
  return [
    a.id === null ? '—' : `#${String(a.id)}`,
    whoCell(a),
    rungCell(a),
    a.listCode === '' ? '— not stated' : a.listCode,
    governsCell(a),
    a.effectiveFrom === '' ? '—' : day(a.effectiveFrom),
    a.effectiveTo === '' ? 'open' : day(a.effectiveTo),
    `${a.createdBy === '' ? '— unattributed' : a.createdBy}${a.reasonCode === '' ? '' : ` · ${a.reasonCode}`}`,
    a.closedBy === ''
      ? (a.open === false ? '— CLOSED WITH NO NAME ON IT' : '— still open')
      : `${a.closedBy}${a.closeReasonCode === '' ? '' : ` · ${a.closeReasonCode}`}`,
  ]
}

export function composeAssignments(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['assignments'])
    ? (payload['assignments'] as readonly unknown[])
    : []
  const rows: Assignment[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readAssignment(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const open = rows.filter((a) => a.open === true).length
  const segments = rows.filter((a) => a.scope === 'segment' && a.open === true).length

  const table: TableBlock = {
    t: 'table',
    title: 'Who is priced away from the default',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} assignment(s) · ${String(open)} open${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'Closed assignments are listed, not filtered out: a closed row is what explains a fee charged six months ago, and a table of only what is open today cannot answer that. Nothing here is edited — an assignment is closed and a new one opened.',
  }

  const blocks: Block[] = [table]

  const note = str(payload['precedence_note'])
  if (note !== '') {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Precedence', title: 'From the backend, verbatim',
      body: note, ref: 'precedence_note',
    })
  }

  if (segments > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'What a segment row does NOT say',
      title: `${String(segments)} open segment assignment(s) — none of them is a verdict about anybody`,
      body: 'This read knows no segment membership, so it cannot tell whether a given customer is in a given segment, and between a customer’s own segments the lowest precedence wins. A segment row therefore says which rung it sits on and how strongly, and never that it prices a particular person. What one customer actually pays is answered by the per-customer read, which is the same function the fee path calls — open it from the customer list.',
      ref: 'governs is served false on every segment row, deliberately',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Empty',
      title: 'Nobody has ever been assigned away from the default price list',
      body: 'This read returns every assignment ever made, open and closed, so an empty table means none was ever recorded — not that none is in force now. Every customer is priced by the default list.',
      ref: 'no assignment rows exist',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} assignment row(s) could not be read`,
      body: 'Counted rather than dropped silently. A row without an id cannot be closed from this screen, and a pricing table quietly one row shorter than the record is one an operator reads as more uniform than it is.',
      ref: 'the count is the finding',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  // Always. The screen must not be read as answering it.
  missing.push('what any one customer actually pays — that is the per-customer read, opened from the customer list')
  missing.push('which customers are in a segment — this read carries no membership')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  return {
    blocks,
    provenance,
    subject: `price_list_assignments · ${String(rows.length)} row(s), ${String(open)} open`,
  }
}

// ---------------------------------------------------------------------------
// The two controls
// ---------------------------------------------------------------------------

const ASSIGN_REASONS: readonly DecideReason[] = [
  ['negotiated_rate', 'A rate agreed with this customer or cohort', ''],
  ['pilot_pricing', 'A limited pricing trial', ''],
  ['retention_offer', 'Pricing agreed to retain them', ''],
  ['segment_policy', 'The pricing policy for this cohort changed', ''],
  ['corrected_assignment', 'The previous assignment was wrong', ''],
]

const CLOSE_REASONS: readonly DecideReason[] = [
  ['agreement_ended', 'The agreement that priced them has ended', ''],
  ['pilot_ended', 'The trial is over', ''],
  ['superseded_by_new_assignment', 'A different list applies from now', ''],
  ['assigned_in_error', 'It should not have been assigned', ''],
]

/**
 * Assigning a price list to a customer or a segment.
 *
 * # There is no PUT, and the shape is the reason
 *
 * `POST` on the collection, never `PUT` on a customer. An assignment is a row
 * with a window, not a property of a person — so assigning again opens a new
 * row and closing is its own act. Nothing here can overwrite what priced a past
 * charge (invariant 9).
 */
export function assignForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM, ROLE, 'Assigning a price list')

  return {
    t: 'form',
    title: 'Assign a price list',
    lead: 'Opens a new assignment. Nothing is overwritten — if one is already open for the same subject, close it first, because what priced a past charge has to keep resolving.',
    fields: [
      {
        name: 'scope', label: 'Who', kind: 'select', required: true,
        options: [['customer', 'customer — one person'], ['segment', 'segment — a cohort']],
        note: 'A customer assignment outranks every segment assignment. A segment one competes with the customer’s other segments, where the LOWEST precedence wins.',
      },
      {
        name: 'customer_id', label: 'Customer id', kind: 'text', required: false, mono: true,
        note: 'Only when the scope is customer.',
      },
      {
        name: 'segment_key', label: 'Segment key', kind: 'text', required: false, mono: true,
        note: 'Only when the scope is segment.',
      },
      {
        name: 'precedence', label: 'Precedence', kind: 'text', required: false, mono: true,
        note: 'Segment scope only, and LOWER IS STRONGER. It orders this assignment against the customer’s other segments; it has no meaning on the customer rung, where nothing outranks it.',
      },
      {
        name: 'list_code', label: 'Price list', kind: 'text', required: true, mono: true,
        note: 'The code of a published list.',
      },
      {
        name: 'effective_from', label: 'Effective from', kind: 'date', required: true,
        note: 'When this assignment starts pricing them. Required — an assignment with no start is one nothing can resolve a past charge against.',
      },
      {
        name: 'reason_code', label: 'Reason code', kind: 'select', required: true,
        options: ASSIGN_REASONS.map(([c, g]) => [c, `${c} — ${g}`] as const),
        note: 'From the managed registry. Stored on the assignment and readable beside it afterwards.',
      },
    ],
    reasons: ASSIGN_REASONS,
    reasonNote: 'Stored on the assignment row and in the audit row. A closed assignment keeps it.',
    auditLine: 'admin.price_list.assign · actor · <scope> · list_code + reason_code + reason',
    cta: 'Assign',
    ctaNote: 'This does not tell you what any one customer will now pay — that resolution runs on the fee path. Open a customer from the customer list to see what prices them.',
    blocked,
    ...(blocked === '' ? { submit: assignSubmit(api) } : {}),
  }
}

function assignSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => {
    const scope = v['scope'] ?? ''
    const body: Record<string, unknown> = {
      scope,
      list_code: (v['list_code'] ?? '').trim(),
      effective_from: v['effective_from'] ?? '',
      reason_code: v['reason_code'] ?? '',
      reason: v['reason'] ?? '',
    }
    // Only the field the scope permits, and precedence only where it means
    // something — a precedence on the customer rung is an order against nothing.
    if (scope === 'customer') body['customer_id'] = Number(v['customer_id'] ?? '')
    if (scope === 'segment') {
      body['segment_key'] = (v['segment_key'] ?? '').trim()
      body['precedence'] = Number(v['precedence'] ?? '')
    }
    return submitWrite(
      () => api.post<unknown>(PRICE_ASSIGNMENTS_PATH, body),
      'The price list is assigned',
      (answer) => lines(answer, [
        ['what they pay', 'not answered here', 'the fee path resolves it — open the customer'],
      ]),
    )
  }
}

/**
 * Closing an assignment.
 *
 * `POST .../close` rather than `DELETE`, because nothing is deleted: the row
 * stays and stops governing. A closed assignment is what explains a fee charged
 * while it was open, and a DELETE could not express that at all.
 */
export function closeAssignmentForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM, ROLE, 'Closing a price-list assignment')

  return {
    t: 'form',
    title: 'Close an assignment',
    lead: 'The row stays and stops pricing them from the date you give. Nothing is deleted — a closed assignment is what explains a fee charged while it was open.',
    fields: [
      {
        name: 'assignment_id', label: 'Assignment #', kind: 'text', required: true, mono: true,
        note: 'The number in the first column above.',
      },
      {
        name: 'effective_to', label: 'Effective to', kind: 'date', required: true,
        note: 'When it stops pricing them. Charges before this date still resolve against it.',
      },
      {
        name: 'reason_code', label: 'Reason code', kind: 'select', required: true,
        options: CLOSE_REASONS.map(([c, g]) => [c, `${c} — ${g}`] as const),
        note: 'From the managed registry. Recorded as the close reason beside the reason it was opened for.',
      },
    ],
    reasons: CLOSE_REASONS,
    reasonNote: 'Recorded against the closure. Who opened it and who closed it both stay readable.',
    auditLine: 'admin.price_list.unassign · actor · assignment:<id> · reason_code + reason',
    cta: 'Close this assignment',
    ctaNote: 'Reversible only by opening a NEW assignment — this one keeps its window and keeps explaining what it priced.',
    blocked,
    ...(blocked === '' ? { submit: closeSubmit(api) } : {}),
  }
}

function closeSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => {
    const id = (v['assignment_id'] ?? '').trim()
    return submitWrite(
      () => api.post<unknown>(`${PRICE_ASSIGNMENTS_PATH}/${encodeURIComponent(id)}/close`, {
        effective_to: v['effective_to'] ?? '',
        reason_code: v['reason_code'] ?? '',
        reason: v['reason'] ?? '',
      }),
      'The assignment is closed',
      (answer) => lines(answer, [['the row', 'kept', 'it still explains what it priced']]),
    )
  }
}

function lines(answer: unknown, extra: readonly FormLine[]): readonly FormLine[] {
  const out: FormLine[] = []
  for (const [key, label] of [
    ['id', 'assignment'], ['scope', 'scope'], ['list_code', 'price list'], ['state', 'state'],
  ] as const) {
    const v = valueAt(answer, key)
    if (v !== '') out.push([label, v, ''])
  }
  const note = valueAt(answer, 'note')
  if (note !== '') out.push(['the backend adds', note, ''])
  return [...out, ...extra]
}

function valueAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const v = (o as Record<string, unknown>)[key]
  if (typeof v === 'string') return v
  if (typeof v === 'number') return String(v)
  return ''
}

export const loadAssignments: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(PRICE_ASSIGNMENTS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The assignment list answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The assignment list returned a shape this console does not know.', null)
  }
  const caller = await callerBeside(api)
  const data = composeAssignments(payload as Record<string, unknown>, new Date())
  return {
    ...data,
    blocks: [...data.blocks, assignForm(api, caller), closeAssignmentForm(api, caller)],
  }
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The price-list assignments could not be read',
      body: `${reason} Nothing is drawn and no control is offered over assignments this console cannot see — assigning without being able to observe the result is the exact state this screen waited for a read to leave. This is NOT an empty list: whether anybody is priced away from the default is unknown from here.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
